import { useState, useCallback, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { CostOverrideType } from '../CostOverrideType';

const excludeById = id => d => d.id !== id;

const buildEmptyDimensions = dimensions =>
  dimensions.reduce(
    (acc, d) => ({ ...acc, [d.id]: { id: uuid(), value: null } }),
    {}
  );

const buildSubEntryDimensions = (dimensions, dimensionIndex, record) =>
  dimensions.reduce(
    (acc, d, i) => ({
      ...acc,
      [d.id]: i < dimensionIndex ? record[d.id] : { id: uuid(), value: null }
    }),
    {}
  );

const createTopLevelEntry = (dimensions, currency) => ({
  ...buildEmptyDimensions(dimensions),
  effectiveDate: null,
  amount: { amount: 0, currency },
  percent: 0,
  type: CostOverrideType.HOURLY_RATE,
  groupId: uuid(),
  id: uuid(),
  isInitialRate: true,
  totalEntries: 1
});

const createSubEntry = (dimensions, currency, dimensionIndex, record) => ({
  ...buildSubEntryDimensions(dimensions, dimensionIndex, record),
  effectiveDate: null,
  amount: { amount: 0, currency },
  percent: 0,
  type: CostOverrideType.HOURLY_RATE,
  groupId: uuid(),
  id: uuid(),
  isInitialRate: true,
  totalEntries: 1
});

const addEmptyDimensionToRates = (rates, dimension) =>
  rates.map(r => ({
    ...r,
    [dimension.id]: { id: uuid(), value: null }
  }));

const stripDimensionFromRate = (rate, dimensionId) => {
  const next = { ...rate };

  delete next[dimensionId];

  return next;
};

const stripDimensionFromAllRates = (rates, dimensionId) =>
  rates.map(r => stripDimensionFromRate(r, dimensionId));

const setDimensionValueAt = (rates, targetIndex, dimensionId, value) => {
  const target = rates[targetIndex];

  return rates
    .filter((r, i) => i === targetIndex || r.groupId !== target.groupId)
    .map(r =>
      r.id === target.id
        ? {
            ...r,
            [dimensionId]: { ...r[dimensionId], value },
            effectiveDate: null,
            isInitialRate: true,
            totalEntries: 1
          }
        : r
    );
};

const setEffectiveDateAt = (rates, targetIndex, value) =>
  rates.map((r, i) => (i === targetIndex ? { ...r, effectiveDate: value } : r));

const setAmountAt = (rates, targetIndex, value) =>
  rates.map((r, i) =>
    i === targetIndex ? { ...r, amount: { ...r.amount, amount: value } } : r
  );

const setCurrencyAt = (rates, targetIndex, value) =>
  rates.map((r, i) =>
    i === targetIndex ? { ...r, amount: { ...r.amount, currency: value } } : r
  );

const setTypeAt = (rates, targetIndex, type) =>
  rates.map((r, i) => (i === targetIndex ? { ...r, type } : r));

const setPercentAt = (rates, targetIndex, value) =>
  rates.map((r, i) => (i === targetIndex ? { ...r, percent: value } : r));

const adjustGroupTotals = (rates, groupId, delta) =>
  rates.map(r =>
    r.groupId === groupId ? { ...r, totalEntries: r.totalEntries + delta } : r
  );

const removeRateAtIndex = (rates, index) => rates.filter((_, i) => i !== index);

const removeRatesByGroupId = (rates, groupId) =>
  rates.filter(r => r.groupId !== groupId);

const insertEntryAt = (rates, atIndex, entry) => {
  const next = [...rates];

  next.splice(atIndex, 0, entry);

  return next;
};

const insertSubEntry = (rates, entry, projectCurrency) => {
  const firstEntryIndex = rates.findIndex(e => e.groupId === entry.groupId);
  const inserted = insertEntryAt(rates, firstEntryIndex, {
    ...entry,
    id: uuid(),
    amount: { amount: 0, currency: projectCurrency },
    percent: 0,
    type: CostOverrideType.HOURLY_RATE,
    isInitialRate: false
  });

  return adjustGroupTotals(inserted, entry.groupId, 1);
};

const removeAndAdjustGroup = (rates, index, entry) => {
  const next = removeRateAtIndex(rates, index);

  return adjustGroupTotals(next, entry.groupId, -1);
};

const initialAddNewEntry = {
  isAdding: false,
  value: null,
  objectType: null
};

export const useProjectCostOverrideDimensionHandlers = ({
  projectCurrency,
  initialSelectedDimensions = [],
  initialProjectRates = []
} = {}) => {
  // Captured once on mount; reset() restores from these so Cancel rolls
  // back to the last persisted snapshot. When a real save mutation lands,
  // refresh the refs after the refetch so the new persisted state becomes
  // the rollback target (matches Formik's enableReinitialize semantics).
  const persistedDimensionsRef = useRef(initialSelectedDimensions);
  const persistedRatesRef = useRef(initialProjectRates);

  const [selectedDimensions, setSelectedDimensions] = useState(
    persistedDimensionsRef.current
  );
  const [projectRates, setProjectRates] = useState(persistedRatesRef.current);
  const [addNewEntry, setAddNewEntry] = useState(initialAddNewEntry);

  const onAddDimension = useCallback(dimension => {
    setSelectedDimensions(prev => [...prev, dimension]);
    setProjectRates(prev => addEmptyDimensionToRates(prev, dimension));
  }, []);

  const onRemoveDimension = useCallback(
    dimension => {
      const exclude = excludeById(dimension.id);
      const willBeEmpty =
        selectedDimensions.length === 1 &&
        selectedDimensions[0].id === dimension.id;

      setSelectedDimensions(prev => prev.filter(exclude));
      setProjectRates(prev =>
        willBeEmpty ? [] : stripDimensionFromAllRates(prev, dimension.id)
      );
    },
    [selectedDimensions]
  );

  const onDimensionValueChange = useCallback(
    ({ index, dimensionId, value }) => {
      setProjectRates(prev =>
        setDimensionValueAt(prev, index, dimensionId, value)
      );
    },
    []
  );

  const onEffectiveDateChange = useCallback((index, value) => {
    setProjectRates(prev => setEffectiveDateAt(prev, index, value));
  }, []);

  const onAmountChange = useCallback((index, value) => {
    setProjectRates(prev => setAmountAt(prev, index, value));
  }, []);

  const onCurrencyChange = useCallback((index, value) => {
    setProjectRates(prev => setCurrencyAt(prev, index, value));
  }, []);

  const onTypeChange = useCallback((index, type) => {
    setProjectRates(prev => setTypeAt(prev, index, type));
  }, []);

  const onPercentChange = useCallback((index, value) => {
    setProjectRates(prev => setPercentAt(prev, index, value));
  }, []);

  const onAddEntry = useCallback(
    (_index, entry) => {
      setProjectRates(prev => insertSubEntry(prev, entry, projectCurrency));
    },
    [projectCurrency]
  );

  const onRemoveEntry = useCallback((index, entry) => {
    setProjectRates(prev => removeAndAdjustGroup(prev, index, entry));
  }, []);

  const onAddNewEntry = useCallback(
    ({ record, dimensionIndex, index, dimensionValue }) => {
      const baseEntry = createSubEntry(
        selectedDimensions,
        projectCurrency,
        dimensionIndex,
        record
      );
      const newEntry = dimensionValue?.id
        ? {
            ...baseEntry,
            [selectedDimensions[dimensionIndex].id]: {
              ...baseEntry[selectedDimensions[dimensionIndex].id],
              value: dimensionValue
            }
          }
        : baseEntry;

      setProjectRates(prev => insertEntryAt(prev, index + 1, newEntry));
    },
    [projectCurrency, selectedDimensions]
  );

  const onDeleteGroup = useCallback(groupId => {
    setProjectRates(prev => removeRatesByGroupId(prev, groupId));
  }, []);

  const onAddNewGroup = useCallback(() => {
    const entry = createTopLevelEntry(selectedDimensions, projectCurrency);

    setProjectRates(prev => [...prev, entry]);
  }, [projectCurrency, selectedDimensions]);

  const reset = useCallback(() => {
    setSelectedDimensions(persistedDimensionsRef.current);
    setProjectRates(persistedRatesRef.current);
    setAddNewEntry(initialAddNewEntry);
  }, []);

  const formOnChange = useMemo(
    () => ({
      onAddDimension,
      onRemoveDimension,
      onDimensionValueChange,
      onEffectiveDateChange,
      onAmountChange,
      onCurrencyChange,
      onTypeChange,
      onPercentChange,
      onAddEntry,
      onRemoveEntry,
      onAddNewEntry,
      onAddNewGroup,
      onDeleteGroup
    }),
    [
      onAddDimension,
      onRemoveDimension,
      onDimensionValueChange,
      onEffectiveDateChange,
      onAmountChange,
      onCurrencyChange,
      onTypeChange,
      onPercentChange,
      onAddEntry,
      onRemoveEntry,
      onAddNewEntry,
      onAddNewGroup,
      onDeleteGroup
    ]
  );

  return {
    selectedDimensions,
    projectRates,
    addNewEntry,
    setAddNewEntry,
    formOnChange,
    reset
  };
};

export default useProjectCostOverrideDimensionHandlers;
